<template>
    <div class="mx-2 mx-lg-5">
         <div class="card mt-3">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h3>{{ $t("headers.guestbooks") }}</h3>
                <div class="d-flex">
                    <button
                        v-if="$store.getters.selectedGatehouse?.settings?.guestbook_overview_export_enabled && $store.getters.hasGatehousePerm('export_guestbook')"
                        class="btn btn-sm btn-light me-3 d-inline-flex align-items-center"
                        @click="openExportModal"
                    >
                        <span class="fs-6 fw-bold">{{ $t("guestbook.export.button") }}</span>
                    </button>
                    <router-link
                        v-if="$store.getters.hasGatehousePerm('add_guestbook')"
                        :to="{ name: 'guestbookAddView' }"
                        class="btn btn-sm btn-success me-3 d-inline-flex align-items-center"
                    >
                        <icon icon="Plus" class="me-2"></icon>
                        <span class="fs-6 fw-bold">{{ $t("utils.addRecord") }}</span>
                    </router-link>
                </div>
            </div>
            <div class="card-body">
                <div class="row mb-5 align-items-end">
                    <div class="col-12 col-md-3">
                        <label class="form-label fw-bold">
                            {{ $t("guestbook.fullName") }}
                        </label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="filters.guest_name"
                            :placeholder="$t('guestbook.fullName')"
                        />
                    </div>

                    <div class="col-12 col-md-2">
                        <label class="form-label fw-bold">
                            {{ $t("status.status") }}
                        </label>
                        <select class="form-select" v-model="filters.status">
                            <option value="">{{ $t("guestbook.statusFilter.all") }}</option>
                            <option value="reserved">{{ $t("status.guestbook.reserved") }}</option>
                            <option value="arrived">{{ $t("guestbook.statusFilter.onSite") }}</option>
                            <option value="left">{{ $t("status.guestbook.left") }}</option>
                        </select>
                    </div>

                    <div class="col-12 col-md-2">
                        <label class="form-label fw-bold">
                            {{ $t("guestbook.from") }}
                        </label>
                        <input
                            type="date"
                            class="form-control"
                            v-model="filters.expected_from"
                        />
                    </div>

                    <div class="col-12 col-md-2">
                        <label class="form-label fw-bold">
                            {{ $t("guestbook.to") }}
                        </label>
                        <input
                            type="date"
                            class="form-control"
                            v-model="filters.expected_to"
                        />
                    </div>

                     <div class="col-12 col-md-2 ms-auto d-flex">
                        <button
                            class="btn btn-secondary w-100"
                            @click="resetFilters"
                        >
                            {{ $t("utils.clearFilters") }}
                        </button>
                    </div>
                </div>


                <GuestbookProvider
                    :api-url="`/${$route.params.slug}/guestbooks`"
                    :filters="filters"
                    v-slot="p"
                >
                    <GuestbookTable
                        :items="p.items"
                        :total-count="p.totalCount"
                        :page="p.page"
                        :per-page="p.perPage"
                        @page-change="p.setPage"
                        @per-page-change="p.setPerPage"
                        @refresh="() => p.refresh()"
                    />
                </GuestbookProvider>
            </div>
         </div>
    </div>

    <div class="modal fade" tabindex="-1" aria-hidden="true" ref="exportModal">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header border-0 mx-2">
                    <h5 class="modal-title">{{ $t("guestbook.export.title") }}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-2">
                    <p class="text-muted">{{ $t("guestbook.export.description") }}</p>
                    <div class="d-flex gap-4 mb-3">
                        <div v-for="format in ['pdf', 'xlsx']" :key="format" class="form-check">
                            <input
                                class="form-check-input"
                                type="radio"
                                :id="`export_format_${format}`"
                                :value="format"
                                v-model="exportFormat"
                            >
                            <label class="form-check-label fw-bold" :for="`export_format_${format}`">
                                {{ $t(`guestbook.export.${format}`) }}
                            </label>
                        </div>
                    </div>
                    <p v-if="exportFormat === 'xlsx'" class="text-muted small">{{ $t("guestbook.export.xlsxNotEmailed") }}</p>
                    <div v-if="exportMessage" class="alert mb-0" :class="exportMessage.error ? 'alert-danger' : 'alert-success'">
                        {{ exportMessage.text }}
                    </div>
                </div>
                <div class="modal-footer border-0">
                    <button class="btn btn-outline-primary" :disabled="sending || exportFormat !== 'pdf'" @click="sendExport">
                        <span v-if="sending" class="spinner-border spinner-border-sm me-1"></span>
                        {{ $t("guestbook.export.send") }}
                    </button>
                    <button class="btn btn-primary" :disabled="downloading" @click="downloadExport">
                        <span v-if="downloading" class="spinner-border spinner-border-sm me-1"></span>
                        {{ $t("guestbook.export.download") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import GuestbookTable from '@/components/GuestbookTable.vue';
import GuestbookProvider from '@/components/GuestbookProvider.vue';
import { Modal } from 'bootstrap';

export default {
    components: {
        Icon,
        GuestbookTable,
        GuestbookProvider,
    },
    data() {
        return {
            filters: {
                guest_name: "",
                status: "",
                expected_from: "",
                expected_to: "",
            },
            exportModal: null,
            exportFormat: "pdf",
            exportMessage: null,
            downloading: false,
            sending: false,
        }
    },
    mounted() {
        this.exportModal = new Modal(this.$refs.exportModal);
    },
    beforeUnmount() {
        if (this.exportModal) this.exportModal.hide();
    },
    methods: {
        resetFilters() {
            this.filters.guest_name = "";
            this.filters.status = "";
            this.filters.expected_from = "";
            this.filters.expected_to = "";
        },
        activeFilters() {
            return Object.fromEntries(Object.entries(this.filters).filter(([, value]) => value));
        },
        exportErrorText(errorCode) {
            const messages = {
                520: "guestbook.export.noRecipients",
                523: "guestbook.export.invalidDate",
                524: "guestbook.export.tooMany",
                525: "guestbook.export.invalidStatus",
            };
            return this.$t(messages[errorCode] || "guestbook.export.error");
        },
        openExportModal() {
            this.exportMessage = null;
            this.exportModal.show();
        },
        downloadExport() {
            this.downloading = true;
            this.exportMessage = null;
            this.$store.getters.api.get(`/${this.$route.params.slug}/guestbooks/export`, {
                params: { ...this.activeFilters(), file_format: this.exportFormat },
                responseType: "blob",
            })
            .then(response => {
                const url = URL.createObjectURL(response.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `navstevy_${this.$route.params.slug}.${this.exportFormat}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            })
            .catch(async error => {
                console.error("Error exporting guestbooks", error);
                // the error of a blob request arrives as a blob as well
                let errorCode;
                try {
                    errorCode = JSON.parse(await error.response.data.text()).error_code;
                } catch {
                    errorCode = undefined;
                }
                this.exportMessage = { error: true, text: this.exportErrorText(errorCode) };
            })
            .finally(() => {
                this.downloading = false;
            });
        },
        sendExport() {
            if (!window.confirm(this.$t("guestbook.export.sendConfirm"))) return;

            this.sending = true;
            this.exportMessage = null;
            this.$store.getters.api.post(`/${this.$route.params.slug}/guestbooks/export/send`, this.activeFilters())
            .then(response => {
                const { recipients, failed } = response.data;
                let text = this.$t("guestbook.export.sent", { emails: recipients.join(", ") });
                if (failed.length) text += " " + this.$t("guestbook.export.failed", { emails: failed.join(", ") });
                this.exportMessage = { error: failed.length > 0, text };
            })
            .catch(error => {
                console.error("Error sending guestbook export", error);
                this.exportMessage = { error: true, text: this.exportErrorText(error.response?.data?.error_code) };
            })
            .finally(() => {
                this.sending = false;
            });
        },
    },
}
</script>
